import React from 'react';
import AuthService from './authService';
import serviceHelper from '../Helpers/serviceHelper';
import OpportunitySecurityCompletionItem from '../Domain/OpportunitySecurityCompletionItem';
import ChecklistCopySourceItem from '../Domain/ChecklistCopySourceItem';

class OpportunitySecurityChecklistService extends React.Component {
  static GetById(opportunityId) {
    return AuthService.getInstance().CallBackendFunction(
      `/api/OpportunitySecurityChecklist/GetByIdAsync/${opportunityId}`
    );
  }

  // Fetched on their own, not bundled into the checklist: the completion log panel owns its list + refresh.
  static GetCompletions(opportunityId, completionType) {
    return AuthService.getInstance().CallBackendFunction(
      `/api/OpportunitySecurityChecklist/GetCompletionsAsync/${Number(opportunityId)}/${completionType}`
    );
  }

  // camelCase API rows -> PascalCase items, mirroring CommentService.ConvertDBItem.
  static ConvertCompletionsDBItem(items) {
    return Array.isArray(items) ? items.map((item) => new OpportunitySecurityCompletionItem(item)) : [];
  }

  // Adds a single append-only Data Retention detail entry (Section B). Retention details are plain Comments on
  // the OpportunitySecurityChecklist entity type, so this posts the generic SaveCommentData shape and reads go
  // through the generic CommentService. The controller also mirrors a breadcrumb onto the Comments tab.
  static AddRetentionDetail(entityTypeId, opportunityId, detail) {
    const url = '/api/OpportunitySecurityChecklist/AddRetentionDetailAsync';
    const body = JSON.stringify({
      EntityTypeId: Number(entityTypeId),
      EntityId: Number(opportunityId),
      NewComment: detail,
    });

    return AuthService.getInstance().CallBackendFunctionPost(url, body);
  }

  // The one write path for the edit wizard, the two imports and a copy from another Opportunity. Create vs update
  // follows the item's own OpportunitySecurityChecklistId; the flags only pick the audit comment, the sole
  // difference between the flows. Each flow's extras ride along as optional item fields.
  static UpsertChecklist(item, isImportFromContract = false, isCopy = false, isImportFromScheduleEd = false) {
    const url =
      `/api/OpportunitySecurityChecklist/UpsertAsync?isImportFromContract=${isImportFromContract}` +
      `&isCopy=${isCopy}&isImportFromScheduleEd=${isImportFromScheduleEd}`;
    const payload = serviceHelper.ConvertAllPropertyToString({ ...item });

    return AuthService.getInstance().CallBackendFunctionPost(url, JSON.stringify(payload));
  }

  // Sends an uploaded contract (PDF/DOCX/JPG/PNG) to the AI extraction operation. What comes back is a partial
  // checklist resource - GetById's own shape, null for whatever the contract did not state - so the caller
  // layers it over the stored checklist without mapping. Nothing is persisted here. The checklist's existing notes
  // go along so the extraction returns only the obligations they do not already state.
  static ExtractFromContract(file, existingNotes) {
    const data = new FormData();
    // 'file' is the key CallBackendFunctionPostFile inspects for its 25 MB guard.
    data.append('file', file);
    data.append('existingNotes', existingNotes ?? '');

    return AuthService.getInstance().CallBackendFunctionPostFile('/api/OpenAiChat/ExtractSecurityChecklist', data);
  }

  // The Opportunity's latest Schedule D/E submission as a partial checklist resource, shaped like the contract
  // extraction, so the same review takes it. Nothing is persisted here.
  static GetScheduleEdImport(opportunityId) {
    return AuthService.getInstance().CallBackendFunction(
      `/api/PersonalizedWebpage/GetOpportunityScheduleEdChecklistImportAsync/${Number(opportunityId)}`
    );
  }

  // The same client's Won Opportunities with a completed checklist, newest first. The eligibility rule lives
  // in the microservice, so the UI never decides what may be copied.
  static GetCopySources(opportunityId) {
    return AuthService.getInstance().CallBackendFunction(
      `/api/OpportunitySecurityChecklist/GetCopySourcesAsync/${Number(opportunityId)}`
    );
  }

  // camelCase API rows -> the picker's data-table items.
  static ConvertCopySourcesDBItem(items) {
    return Array.isArray(items) ? items.map((item) => new ChecklistCopySourceItem(item)) : [];
  }

  // Adds a single Data Transfer / Data Erasure entry. Immutable - no edit/delete counterpart by design.
  static AddCompletion(opportunityId, completionType, note) {
    const url = '/api/OpportunitySecurityChecklist/AddCompletionAsync';
    const body = JSON.stringify({
      OpportunityId: Number(opportunityId),
      CompletionType: completionType,
      Note: note,
    });

    return AuthService.getInstance().CallBackendFunctionPost(url, body);
  }
}

export default OpportunitySecurityChecklistService;
